/**
 * Math Calculator Utility
 * Evaluates math expressions following BODMAS/PEMDAS order of operations
 */

/**
 * Evaluates a math expression safely
 * @param {string} expression - The math expression to evaluate
 * @returns {object} - { isValid: boolean, result: number|null, displayText: string }
 */
export const evaluateMathExpression = (expression) => {
  if (!expression || typeof expression !== 'string') {
    return { isValid: false, result: null, displayText: expression };
  }

  // Trim whitespace
  const trimmedExpression = expression.trim();

  // If it's just a number, return it
  if (/^-?\d*\.?\d+$/.test(trimmedExpression)) {
    const num = parseFloat(trimmedExpression);
    return { isValid: true, result: num, displayText: trimmedExpression };
  }

  // Only allow numbers, operators, parentheses, decimal points, and whitespace
  const validPattern = /^[\d+\-*/(). ]+$/;

  if (!validPattern.test(trimmedExpression)) {
    return { isValid: false, result: null, displayText: trimmedExpression };
  }

  // Check for balanced parentheses
  let openCount = 0;
  for (let char of trimmedExpression) {
    if (char === '(') openCount++;
    if (char === ')') openCount--;
    if (openCount < 0) {
      return { isValid: false, result: null, displayText: trimmedExpression };
    }
  }
  if (openCount !== 0) {
    return { isValid: false, result: null, displayText: trimmedExpression };
  }

  try {
    // Use Function constructor instead of eval for safer evaluation
    // This follows BODMAS automatically as it's built into JavaScript
    const result = Function(`'use strict'; return (${trimmedExpression})`)();

    // Validate result is a number
    if (typeof result !== 'number' || !isFinite(result)) {
      return { isValid: false, result: null, displayText: trimmedExpression };
    }

    // Round to 2 decimal places for display
    const roundedResult = Math.round(result * 100) / 100;
    const displayText = `${trimmedExpression} = ${roundedResult}`;

    return { isValid: true, result: roundedResult, displayText };
  } catch (error) {
    return { isValid: false, result: null, displayText: trimmedExpression };
  }
};

/**
 * Formats the input value for display in the input field
 * @param {string} value - The current input value
 * @returns {string} - Formatted display value
 */
export const formatMathInput = (value) => {
  const evaluation = evaluateMathExpression(value);
  return evaluation.displayText;
};

/**
 * Gets the numeric value from a math expression
 * @param {string} value - The input value (can be a math expression)
 * @returns {number|string} - The calculated result or original value
 */
export const getMathValue = (value) => {
  const evaluation = evaluateMathExpression(value);
  return evaluation.isValid && evaluation.result !== null ? evaluation.result : value;
};
